// ChargerPayoutTab.js
// Per-charger Payout tab: pick a month (last 18 months), preview the payout
// summary on screen, and download it as a styled Excel (Summary + Transactions)
// or a branded PDF. Reuses the backend payout engine's figures.
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import Token from "../../util/Token";
import { getChargerPayoutReport } from "src/react-query/endPoints";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import {
  buildPayoutSummaryRows,
  buildPaymentTypeSections,
  sumOverDelivered,
  fmtTime,
} from "../payout/payoutShared";

const PAYOUT_RULE_LABELS = {
  1: "Fixed amount per month or % per unit (whichever is higher)",
  2: "Fixed amount per month or fixed per unit (whichever is higher)",
  3: "Fixed amount per month + % per unit",
  4: "Fixed amount per month + fixed per unit",
  5: "% per unit",
  6: "Fixed per unit",
  7: "Fixed amount per month",
};

// Human-readable payout rule with the exact configured rate, e.g.
// "% per unit — 10% + 18% GST" or "Fixed per unit — 2.50/kWh + 18% GST".
const formatPayoutRule = (meta) => {
  if (!meta) return "-";
  const base =
    PAYOUT_RULE_LABELS[meta.payoutRule] || String(meta.payoutRule || "-");
  const pct = Number(meta.commissionPercent);
  const cost = Number(meta.commissionCost);
  const gst = Number(meta.taxPercent);
  const gstSuffix = gst > 0 ? ` + ${gst}% GST` : "";
  if ([1, 3, 5].includes(meta.payoutRule) && pct > 0) {
    return `${base} — ${pct}%${gstSuffix}`;
  }
  if ([2, 4, 6].includes(meta.payoutRule) && cost > 0) {
    return `${base} — ${cost}/kWh${gstSuffix}`;
  }
  return base;
};

// Excel style tokens (navy headers + yellow highlight, mirroring the sample).
const NAVY = "1F3864";
const YELLOW = "FFF2A8";
const LIGHT = "EAF0F8";
const THIN_BORDER = {
  top: { style: "thin", color: { rgb: "D0D7E2" } },
  bottom: { style: "thin", color: { rgb: "D0D7E2" } },
  left: { style: "thin", color: { rgb: "D0D7E2" } },
  right: { style: "thin", color: { rgb: "D0D7E2" } },
};
const S = {
  title: { font: { bold: true, sz: 16, color: { rgb: NAVY } } },
  subtitle: { font: { bold: true, sz: 12, color: { rgb: "333333" } } },
  meta: { font: { sz: 10, color: { rgb: "666666" } } },
  section: {
    font: { bold: true, sz: 11, color: { rgb: NAVY } },
    fill: { fgColor: { rgb: LIGHT } },
  },
  header: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center" },
    border: THIN_BORDER,
  },
  boxLabel: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: NAVY } },
    alignment: { horizontal: "left", vertical: "center" },
    border: THIN_BORDER,
  },
  label: { font: { bold: false }, border: THIN_BORDER },
  labelBold: { font: { bold: true }, border: THIN_BORDER },
  money: {
    numFmt: "#,##0.00",
    alignment: { horizontal: "right" },
    border: THIN_BORDER,
  },
  moneyBold: {
    numFmt: "#,##0.00",
    font: { bold: true },
    alignment: { horizontal: "right" },
    border: THIN_BORDER,
  },
  number: {
    numFmt: "#,##0",
    alignment: { horizontal: "right" },
    border: THIN_BORDER,
  },
  highlight: {
    numFmt: "#,##0.00",
    font: { bold: true },
    fill: { fgColor: { rgb: YELLOW } },
    alignment: { horizontal: "right" },
    border: THIN_BORDER,
  },
  note: { font: { italic: true, sz: 9, color: { rgb: "666666" } } },
  lightLabel: {
    font: { bold: true },
    fill: { fgColor: { rgb: LIGHT } },
    border: THIN_BORDER,
  },
};

const money = (n) => (Number(n) || 0).toFixed(2);
const withComma = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Single source of truth for the Charging Summary field names, so the on-screen
// view, Excel and PDF always show identical labels.
const buildChargingSummary = (summary, byPaymentType = []) => {
  const rows = [
    { label: "Total Transactions", value: summary.totalTransactions, int: true },
    { label: "Total kWh Consumed", value: summary.totalKWh },
  ];
  // kWh breakdown by settlement bucket (payment collected / OCPI / RFID), shown
  // only when some energy was settled outside Amik.
  const byPT = byPaymentType || [];
  if (byPT.length) {
    const kwhOf = (type) => {
      const p = byPT.find((x) => x.paymentType === type);
      return p ? Number(p.totalKWh) || 0 : 0;
    };
    const rfidKWh = kwhOf("RFID");
    const ocpiKWh = kwhOf("OCPI");
    const collectedKWh = (Number(summary.totalKWh) || 0) - rfidKWh - ocpiKWh;
    if (rfidKWh > 0.0005 || ocpiKWh > 0.0005) {
      rows.push({ label: "   • Payment Collected (kWh)", value: collectedKWh, sub: true });
      if (ocpiKWh > 0.0005)
        rows.push({ label: "   • OCPI (kWh)", value: ocpiKWh, sub: true });
      if (rfidKWh > 0.0005)
        rows.push({ label: "   • RFID (kWh)", value: rfidKWh, sub: true });
    }
  }
  rows.push(
    { label: "Avg Price per kWh (excl. GST)", value: summary.avgPricePerKwExclGst },
    { label: "+ GST per kWh", value: summary.avgGstPerKw },
    {
      label: "= Avg Price per kWh (incl. GST)",
      value: summary.avgPricePerKw,
      bold: true,
    },
    { label: "Total Consumption Cost", value: summary.totalConsumptionCost },
    { label: "Total GST", value: summary.totalTaxAmount },
    { label: "Total Charging Amount Collected", value: summary.totalChargingCost }
  );
  return rows;
};

// "Type" label for a payout transaction — OCPI rows show their roaming company.
const txnTypeLabel = (t) =>
  t.paymentType === "OCPI" && t.ocpiCompany
    ? `OCPI · ${t.ocpiCompany}`
    : t.paymentType;

/**
 * Loads the Amik logo as a PNG data URL for embedding in the PDF. Rasterises the
 * SVG via a canvas; resolves null (PDF still generates without a logo) on failure.
 */
const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const w = img.naturalWidth || 240;
          const h = img.naturalHeight || 90;
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = "/logo/amikLogo.svg";
    } catch (e) {
      resolve(null);
    }
  });

export const ChargerPayoutTab = ({
  teamId,
  chargerPk,
  chargepointId,
  chargerName,
}) => {
  const domainConfig = useDomainConfig();
  const brand = domainConfig?.name || "Amik";
  const resolvedTeamId =
    teamId && typeof teamId === "object" ? teamId.id : teamId;
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Last 18 months as {value, year, month, label}.
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 18; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      opts.push({
        value: `${year}-${month}`,
        year,
        month,
        label: `${d.toLocaleString("en-US", { month: "long" })} ${year}`,
      });
    }
    return opts;
  }, []);

  const selectedOption = monthOptions.find((m) => m.value === selected);
  const periodLabel = selectedOption?.label || "";

  const handleGenerate = async () => {
    if (!selectedOption) {
      setError("Please select a month to generate the payout report.");
      return;
    }
    if (!chargerPk || !resolvedTeamId) {
      setError("Charger or team reference is missing.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setData(null);
      const token = Token.get();
      const response = await axios.get(
        getChargerPayoutReport(
          resolvedTeamId,
          chargerPk,
          selectedOption.year,
          selectedOption.month
        ),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || "Failed to generate payout report.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to generate payout report."
      );
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------- Excel ----
  const handleDownloadExcel = () => {
    if (!data) return;
    const { summary, payout, byConnector, byPaymentType, meta, transactions } =
      data;
    const cur = meta?.currency || "INR";

    // Build the Summary sheet as a dense grid, then style specific cells.
    const rows = [];
    const merges = [];
    const styleMap = {}; // "R,C" -> style
    const setStyle = (rr, cc, s) => {
      styleMap[`${rr},${cc}`] = s;
    };
    const ensure = (rr) => {
      while (rows.length <= rr) rows.push([]);
      return rows[rr];
    };
    const put = (rr, cc, v, s) => {
      const row = ensure(rr);
      while (row.length <= cc) row.push("");
      row[cc] = v;
      if (s) setStyle(rr, cc, s);
    };
    // Fill a full 4-col row header band so the section fill spans the width.
    const band = (rr, label, s) => {
      put(rr, 0, label, s);
      for (let c = 1; c <= 3; c++) put(rr, c, "", s);
      merges.push({ s: { r: rr, c: 0 }, e: { r: rr, c: 3 } });
    };

    let r = 0;
    band(r, "PAYOUT REPORT", S.title);
    r += 1;
    band(
      r,
      `${chargerName || ""}${chargepointId ? ` (${chargepointId})` : ""}`,
      S.subtitle
    );
    r += 1;
    band(
      r,
      `Period: ${periodLabel}    |    Currency: ${cur}    |    Generated: ${new Date().toLocaleDateString(
        "en-IN"
      )}`,
      S.meta
    );
    r += 2;

    // Charging Summary.
    band(r, "CHARGING SUMMARY", S.section);
    r += 1;
    buildChargingSummary(summary, byPaymentType).forEach((m) => {
      put(r, 0, m.label, S.label);
      put(
        r,
        1,
        Number(m.value) || 0,
        m.int ? S.number : m.bold ? S.moneyBold : S.money
      );
      r += 1;
    });
    r += 1;

    // Payout Summary — both excl. and incl. GST.
    band(r, "PAYOUT SUMMARY", S.section);
    r += 1;
    put(r, 0, "Item", S.header);
    put(r, 1, "Without GST", S.header);
    put(r, 2, "With GST", S.header);
    r += 1;
    buildPayoutSummaryRows(
      summary,
      payout,
      meta,
      brand,
      sumOverDelivered(transactions),
      byPaymentType
    ).forEach((pr) => {
      put(r, 0, pr.label, pr.highlight ? S.labelBold : S.label);
      put(r, 1, Number(pr.woGst) || 0, pr.highlight ? S.moneyBold : S.money);
      put(r, 2, Number(pr.wGst) || 0, pr.highlight ? S.highlight : S.moneyBold);
      r += 1;
    });
    put(r, 0, `Payout = Total Collection By Amik − ${brand} CMS Fee`, S.note);
    r += 2;

    // Breakdown by Connector.
    band(r, "BREAKDOWN BY CONNECTOR", S.section);
    r += 1;
    ["Connector", "Transactions", "Total kWh", "Charging Amount Collected"].forEach(
      (h, c) => put(r, c, h, S.header)
    );
    r += 1;
    byConnector.forEach((cn) => {
      put(r, 0, cn.connectorId, S.label);
      put(r, 1, Number(cn.transactions) || 0, S.number);
      put(r, 2, Number(cn.totalKWh) || 0, S.money);
      put(r, 3, Number(cn.totalChargingCost) || 0, S.money);
      r += 1;
    });
    r += 1;

    // Charging by settlement bucket: collected, RFID, and OCPI per roaming company.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      band(r, sec.title.toUpperCase(), S.section);
      r += 1;
      [sec.firstColLabel, "Transactions", "Charging Amount Collected"].forEach(
        (h, c) => put(r, c, h, S.header)
      );
      r += 1;
      let secTxns = 0;
      let secCharging = 0;
      if (sec.rows.length) {
        sec.rows.forEach((p) => {
          put(r, 0, p.paymentType, S.label);
          put(r, 1, Number(p.transactions) || 0, S.number);
          put(r, 2, Number(p.totalChargingCost) || 0, S.money);
          secTxns += Number(p.transactions) || 0;
          secCharging += Number(p.totalChargingCost) || 0;
          r += 1;
        });
      } else {
        put(r, 0, sec.emptyText, S.label);
        r += 1;
      }
      put(r, 0, "Total", S.labelBold);
      put(r, 1, secTxns, S.number);
      put(r, 2, Number(money(secCharging)), S.moneyBold);
      r += 2;
    });

    // Notes.
    put(r, 0, `Payout rule: ${formatPayoutRule(meta)}`, S.note);
    r += 1;
    put(
      r,
      0,
      "Total Collection By Amik includes GST and covers all payment types.",
      S.note
    );
    r += 1;
    put(
      r,
      0,
      "Price per kWh is GST-inclusive (Total kWh x Price = Charging Amount Collected).",
      S.note
    );

    const ws = XLSX.utils.aoa_to_sheet(rows);
    Object.entries(styleMap).forEach(([key, s]) => {
      const [rr, cc] = key.split(",").map(Number);
      const addr = XLSX.utils.encode_cell({ r: rr, c: cc });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = s;
    });
    ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }, { wch: 22 }];

    // Transactions sheet (no idTag / email / phone).
    const txnHeader = [
      "Txn ID",
      "Start Time",
      "Stop Time",
      "Connector",
      "kWh",
      "Price/kWh",
      "Payment Type",
      "Payment Gateway",
      "Amount Paid",
      "Refund",
      "Charging Amount Collected",
      "Tax",
      "Consumption Cost",
      "PG Fee",
      "Collection (excl PG)",
      "Currency",
    ];
    const txnRows = (transactions || []).map((t) => [
      t.transactionId,
      fmtTime(t.startTime),
      fmtTime(t.stopTime),
      t.connectorId,
      t.kWh,
      t.pricePerKw,
      txnTypeLabel(t),
      t.paymentGateway || "",
      t.amountPaid,
      t.refund,
      t.chargingCost,
      t.taxAmount,
      t.consumptionCost,
      t.pgFee,
      t.collection,
      t.currency,
    ]);
    const txnSheet = XLSX.utils.aoa_to_sheet([txnHeader, ...txnRows]);
    txnHeader.forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (txnSheet[addr]) txnSheet[addr].s = S.header;
    });
    txnSheet["!cols"] = [
      { wch: 10 },
      { wch: 20 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 14 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 22 },
      { wch: 12 },
      { wch: 16 },
      { wch: 10 },
      { wch: 18 },
      { wch: 10 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    XLSX.utils.book_append_sheet(wb, txnSheet, "Transactions");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Payout_${chargepointId || "charger"}_${periodLabel.replace(" ", "_")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ----------------------------------------------------------------- PDF ----
  const handleDownloadPDF = async () => {
    if (!data) return;
    const { summary, payout, byConnector, byPaymentType, meta, transactions } =
      data;
    const cur = meta?.currency || "INR";
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const logo = await loadLogoDataUrl();
    if (logo?.dataUrl) {
      const w = 34;
      const h = Math.max(10, (logo.height / logo.width) * w);
      try {
        doc.addImage(logo.dataUrl, "PNG", 14, 10, w, h);
      } catch (e) {
        /* logo optional */
      }
    }

    doc.setFontSize(16);
    doc.setTextColor(31, 56, 100);
    doc.text("Payout Report", pageWidth - 14, 18, { align: "right" });
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(`${brand}`, pageWidth - 14, 25, { align: "right" });

    doc.setFontSize(10);
    doc.setTextColor(40);
    let y = 40;
    doc.text(`Charger: ${chargerName || ""} (${chargepointId || ""})`, 14, y);
    doc.text(`Period: ${periodLabel}`, 14, y + 6);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 14, y + 12);
    doc.text(`Currency: ${cur}`, 14, y + 18);

    // Charging Summary.
    autoTable(doc, {
      startY: 64,
      head: [["Charging Summary", cur]],
      body: buildChargingSummary(summary, byPaymentType).map((m) => [
        m.label,
        m.int ? String(m.value) : withComma(m.value),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: { 1: { halign: "right" } },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    // Payout Summary — both excl. and incl. GST.
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Payout Summary", "Without GST", "With GST"]],
      body: buildPayoutSummaryRows(
        summary,
        payout,
        meta,
        brand,
        sumOverDelivered(transactions),
        byPaymentType
      ).map((pr) => [pr.label, withComma(pr.woGst), withComma(pr.wGst)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right", fontStyle: "bold" },
      },
      theme: "grid",
      margin: { left: 14, right: 14 },
      didParseCell: (d) => {
        if (d.section === "body" && d.row.raw[0] === "Payout") {
          d.cell.styles.fillColor = [255, 242, 168];
          d.cell.styles.fontStyle = "bold";
        }
      },
    });

    // Breakdown by Connector.
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [
        ["Connector", "Transactions", "Total kWh", "Charging Amount Collected"],
      ],
      body: byConnector.map((c) => [
        c.connectorId,
        c.transactions,
        withComma(c.totalKWh),
        withComma(c.totalChargingCost),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100] },
      theme: "grid",
      margin: { left: 14 },
    });

    // Charging by settlement bucket: collected, RFID, and OCPI per roaming company.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      const secTxns = sec.rows.reduce(
        (a, p) => a + (Number(p.transactions) || 0),
        0
      );
      const secCharging = sec.rows.reduce(
        (a, p) => a + (Number(p.totalChargingCost) || 0),
        0
      );
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [[sec.firstColLabel, "Transactions", "Charging Amount Collected"]],
        body: sec.rows.length
          ? sec.rows.map((p) => [
              p.paymentType,
              p.transactions,
              withComma(p.totalChargingCost),
            ])
          : [[sec.emptyText, "", ""]],
        foot: [["Total", secTxns, withComma(secCharging)]],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [31, 56, 100] },
        footStyles: {
          fillColor: [234, 240, 248],
          textColor: 20,
          fontStyle: "bold",
        },
        theme: "grid",
        margin: { left: 14 },
      });
    });

    doc.setFontSize(8);
    doc.setTextColor(120);
    const noteY = doc.lastAutoTable.finalY + 8;
    doc.text(`Payout rule: ${formatPayoutRule(meta)}`, 14, noteY);
    doc.text(
      `Payout = Total Collection By Amik - ${brand} CMS Fee. Total Collection By Amik includes GST and covers all payment types. Price per kWh is GST-inclusive.`,
      14,
      noteY + 5,
      { maxWidth: pageWidth - 28 }
    );

    doc.save(
      `Payout_${chargepointId || "charger"}_${periodLabel.replace(" ", "_")}.pdf`
    );
  };

  // --------------------------------------------------------------- render ---
  return (
    <Box sx={{ p: 1 }}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Payout Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a month to view this charger&apos;s payout summary. A session is
          counted in the month it completed. Download as Excel (summary + all
          transactions) or PDF.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="payout-month-label">Month</InputLabel>
            <Select
              labelId="payout-month-label"
              label="Month"
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                setData(null);
                setError(null);
              }}
            >
              {monthOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !selected}
          >
            {loading ? <CircularProgress size={22} /> : "Generate Report"}
          </Button>
          {data && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
              >
                PDF
              </Button>
            </>
          )}
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Card>

      {data && (
        <>
          <Card sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Charging Summary
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {buildChargingSummary(data.summary, data.byPaymentType).map((m) => (
                    <TableRow key={m.label}>
                      <TableCell
                        sx={{
                          fontWeight: m.bold ? 700 : 400,
                          color: m.sub ? "text.secondary" : "inherit",
                        }}
                      >
                        {m.label}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: m.bold ? 700 : 400 }}
                      >
                        {m.int ? m.value : withComma(m.value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Card sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payout Summary
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Without GST</TableCell>
                    <TableCell align="right">With GST</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buildPayoutSummaryRows(
                    data.summary,
                    data.payout,
                    data.meta,
                    brand,
                    sumOverDelivered(data.transactions),
                    data.byPaymentType
                  ).map((pr) => (
                    <TableRow
                      key={pr.label}
                      sx={
                        pr.highlight
                          ? {
                              bgcolor: "warning.lighter",
                              "& td": { fontWeight: 700 },
                            }
                          : undefined
                      }
                    >
                      <TableCell>{pr.label}</TableCell>
                      <TableCell align="right">{withComma(pr.woGst)}</TableCell>
                      <TableCell align="right">{withComma(pr.wGst)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Payout = Total Collection By Amik − {brand} CMS Fee
            </Typography>
          </Card>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Breakdown by Connector
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Connector</TableCell>
                        <TableCell align="right">Txns</TableCell>
                        <TableCell align="right">kWh</TableCell>
                        <TableCell align="right">Charging Amount Collected</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.byConnector.map((c) => (
                        <TableRow key={c.connectorId}>
                          <TableCell>{c.connectorId}</TableCell>
                          <TableCell align="right">{c.transactions}</TableCell>
                          <TableCell align="right">{withComma(c.totalKWh)}</TableCell>
                          <TableCell align="right">
                            {withComma(c.totalChargingCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2 }}>
                {buildPaymentTypeSections(
                  data.byPaymentType,
                  data.transactions
                ).map((sec, i) => {
                  const secTxns = sec.rows.reduce(
                    (a, p) => a + (Number(p.transactions) || 0),
                    0
                  );
                  const secCharging = sec.rows.reduce(
                    (a, p) => a + (Number(p.totalChargingCost) || 0),
                    0
                  );
                  return (
                    <div key={sec.key} style={{ marginTop: i ? 16 : 0 }}>
                      <Typography variant="subtitle2">{sec.title}</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{sec.firstColLabel}</TableCell>
                              <TableCell align="right">Txns</TableCell>
                              <TableCell align="right">Charging Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sec.rows.length ? (
                              sec.rows.map((p) => (
                                <TableRow key={p.paymentType}>
                                  <TableCell>{p.paymentType}</TableCell>
                                  <TableCell align="right">
                                    {p.transactions}
                                  </TableCell>
                                  <TableCell align="right">
                                    {withComma(p.totalChargingCost)}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {sec.emptyText}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                              <TableCell>Total</TableCell>
                              <TableCell align="right">{secTxns}</TableCell>
                              <TableCell align="right">
                                {withComma(secCharging)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  );
                })}
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              size="small"
              label={`Payout rule: ${formatPayoutRule(data.meta)}`}
            />
            <Chip size="small" label={`Period: ${periodLabel}`} />
            <Chip size="small" label={`Currency: ${data.meta?.currency || "INR"}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Payout = Total Collection By Amik − {brand} CMS Fee. Total Collection
            By Amik includes GST and covers all payment types. Price per kWh is
            GST-inclusive (Total kWh × Price = Charging Amount Collected).
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ChargerPayoutTab;
